<template>
  <div class="guard-callback-form">
    <p
      class="guard-callback-form__prompt"
      v-for="text of promptMessage.split('\n')"
      :key="text"
    >
      {{ text }}
    </p>

    <div class="guard-callback-form__actions" v-if="step === 1 || step === 3">
      <button class="guard-callback-form__btn" @click="clickOnValidate()">
        <span>{{ step === 1 ? "Si. Por favor!" : "Si, es correcto" }}</span>
      </button>
      <button
        class="guard-callback-form__btn guard-callback-form__btn--cancel"
        @click="clickOnCancel()"
      >
        <span>{{ step === 1 ? "No, gracias." : "No, reingresar" }}</span>
      </button>
    </div>

    <div class="guard-callback-form__keyboard-zone" v-show="step === 2">
      <input
        v-model="phoneNumber"
        class="guard-callback-form__input"
        readonly
        placeholder="Indique su número de teléfono"
      />
      <div ref="keyboard" class="guard-callback-form__keyboard"></div>
      <button
        class="guard-callback-form__send"
        @click="onSubmitNumber"
        :disabled="!phoneNumber"
      >
        Enviar
      </button>
    </div>
  </div>
</template>

<script>
import Keyboard from "simple-keyboard";
import "simple-keyboard/build/css/index.css";

const PROMPT_ASK =
  "El tiempo de respuesta puede tardar unos minutos más.\n¿Deseas dejar tu número para contactarte más tarde?";
const PROMPT_TYPE = "Por favor, digite su número de teléfono.";
const PROMPT_CONFIRM = (number) =>
  `He capturado el número ${number}. ¿Es correcto?`;
const PROMPT_SAVED =
  "Gracias. Hemos guardado su número de teléfono. Le contactaremos pronto.";
const PROMPT_DECLINED = "Entendido, que tenga un buen día.";
const PROMPT_TIMEOUT =
  "No se completó el proceso a tiempo. El sistema ha cancelado la solicitud.";
const PROMPT_TOO_MANY =
  "Se han detectado demasiados intentos fallidos. El proceso ha sido cancelado.";

export default {
  name: "CallbackForm",
  props: {
    sublocationId: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      promptMessage: PROMPT_ASK,
      phoneNumber: "",
      step: 1,
      attempts: 0,
      maxAttempts: 3,
      failed: false,
      globalTimeOut: null,
      keyboard: null,
    };
  },
  mounted() {
    this.startGlobalTimeout();
    this.keyboard = new Keyboard(this.$refs.keyboard, {
      layout: {
        default: ["1 2 3", "4 5 6", "7 8 9", "{shift} 0 {bksp}"],
      },
      display: {
        "{bksp}": "⌫",
        "{shift}": "Cancelar",
      },
      theme: "hg-theme-default guard-callback-form__keyboard",
      onChange: (input) => {
        this.phoneNumber = input;
      },
      onKeyPress: (button) => {
        if (button === "{shift}") {
          this.clickOnCancel();
        }
      },
    });
  },
  beforeDestroy() {
    this.clearGlobalTimeout();
    if (this.keyboard) {
      this.keyboard.destroy();
      this.keyboard = null;
    }
  },
  methods: {
    onSubmitNumber() {
      if (!this.phoneNumber) return;
      this.step = 3;
      const formatted = this.phoneNumber.split("").join(" ");
      this.promptMessage = PROMPT_CONFIRM(formatted);
    },
    clickOnValidate() {
      if (this.step === 1) {
        this.step = 2;
        this.promptMessage = PROMPT_TYPE;
      } else if (this.step === 3) {
        this.step = 4;
        this.showKeyboard = false;
        this.promptMessage = PROMPT_SAVED;
        this.savePhoneNumber();
      }
    },
    clickOnCancel() {
      if (this.step === 3) {
        this.attempts++;
        if (this.attempts >= this.maxAttempts) {
          this.fail(PROMPT_TOO_MANY);
          return;
        }
        this.phoneNumber = "";
        if (this.keyboard) this.keyboard.clearInput();
        this.step = 2;
        this.promptMessage = PROMPT_TYPE;
        return;
      }
      this.fail(PROMPT_DECLINED);
    },
    fail(message) {
      this.failed = true;
      this.promptMessage = message;
      this.$emit("close");
    },
    clearGlobalTimeout() {
      if (this.globalTimeOut) {
        clearTimeout(this.globalTimeOut);
        this.globalTimeOut = null;
      }
    },
    startGlobalTimeout() {
      this.clearGlobalTimeout();
      this.globalTimeOut = setTimeout(() => {
        this.fail(PROMPT_TIMEOUT);
      }, 90000);
    },
    savePhoneNumber() {
      const number = Number(this.phoneNumber);
      this.$http
        .get("company_monitorings/get_number_virtual_agent", {
          headers: {
            "X-Device-ID": this.getDeviceId(),
            Authorization: process.env.VUE_APP_JITSI_TOKEN_AUTHORIZATION,
          },
          params: {
            mobile_virtual_agent: number,
            sublocation_id: this.sublocationId,
          },
        })
        .then(() => {
          this.$emit("close");
        })
        .catch((err) => {
          console.error("something went wrong saving the number", err);
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import "../styles";

.guard-callback-form {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  width: 100%;

  &__prompt {
    font-size: 0.82rem;
    color: $guard-mute-blue-text;
    line-height: 1.5;
    margin: 0;
  }

  &__actions {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
  }
  &__btn {
    @include guard-button($guard-cyan-500, $guard-cyan-600, $guard-radius-sm);
    padding: 0.6rem 1.2rem;
    font-size: 0.86rem;
    flex: 1;
    min-width: 5rem;

    &--cancel {
      @include guard-button(
        transparent,
        rgba($guard-accent-red-btn, 0.2),
        $guard-radius-sm,
        $guard-accent-red
      );
      border: 1px solid rgba($guard-accent-red, 0.4);
    }
  }

  &__keyboard-zone {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-top: 0.4rem;
    padding: 0.9rem;
    background: rgba(#fff, 0.05);
    border: 1px solid rgba(#fff, 0.1);
    border-radius: $guard-radius-md;
  }
  &__input {
    width: 100%;
    padding: 0.6rem 0.8rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(#fff, 0.15);
    border-radius: $guard-radius-sm;
    color: $guard-mute-blue-text;
    font-size: 1rem;
    outline: none;
  }
  &__send {
    @include guard-button($guard-cyan-500, $guard-cyan-600, $guard-radius-sm);
    padding: 0.6rem 1rem;
    font-size: 0.86rem;
    align-self: flex-end;
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__keyboard {
    ::v-deep .hg-button {
      background: $guard-navy-800;
      color: #fff;
      border: 1px solid rgba(#fff, 0.1);
      border-radius: $guard-radius-sm;
      font-size: 1.1rem;
      height: 2.8rem;
      &:hover {
        background: $guard-navy-700;
      }
    }
    ::v-deep .hg-theme-default.hg-layout-default {
      background: transparent;
      gap: 4px;
      padding: 4px;
    }
  }
}
</style>
